import React, { useState } from 'react';
import {
  Grid,
  TextField,
  Button,
  FormControl,
  Autocomplete,
} from '@mui/material';
import conditionCategory from '../../../config/ConditionCategory';

const DualScatterplotsSettingsPanel = ({
  classes,
  scatterplotOptionsData = [],
  onSubmit
}) => {

    const [settings, setSettings] = useState({
        selectConditionCategory: null,
        selectXAxisAnalyte: '',
        selectYAxisAnalyte: ''
    });

    const conditionCategoryOptions = Array.from(
        new Set(
        scatterplotOptionsData
            .flatMap(item => item.conditionCategory || [])
            .filter(Boolean)
        )
    );

    // Map options to category objects with value and label
    const displayOptions = conditionCategoryOptions
        .map(key => ({
            value: key,
            label: conditionCategory[key] || key
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    // Find the single analytes for selected category (no comma in analyte_code)
    const selectedCategoryObj = settings?.selectConditionCategory || null;
    const selectedCategoryValue = selectedCategoryObj?.value || '';

    // X-Axis: Filter and map single analyte options based on selected category
    const xAxisAnalyteOptions = scatterplotOptionsData
        .filter(item =>
            item.conditionCategory?.includes(selectedCategoryValue) &&
            !item.analyte_code.includes(',')
        )
        .map(item => ({
            value: item.analyte_code,
            label: item.name
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    // Y-Axis: all analytes for selected category, excluding selected X-Axis analyte
    const yAxisAnalyteOptionsRaw = scatterplotOptionsData
    .filter(item =>
        item.conditionCategory?.includes(selectedCategoryValue) &&
        item.analyte_code !== settings?.selectXAxisAnalyte
    );

    // Remove duplicates by analyte_code
    const uniqueYAxisAnalyteOptionsRaw = Object.values(
    yAxisAnalyteOptionsRaw.reduce((acc, item) => {
        acc[item.analyte_code] = item;
        return acc;
    }, {})
    );

    // Map to options
    const yAxisAnalyteOptionsMapped = uniqueYAxisAnalyteOptionsRaw.map(item => ({
    value: item.analyte_code,
    label: item.name
    }));

    // Split into singles and multiples
    const singles = yAxisAnalyteOptionsMapped
    .filter(opt => !opt.value.includes(','))
    .sort((a, b) => a.label.localeCompare(b.label));

    const multiples = yAxisAnalyteOptionsMapped
    .filter(opt => opt.value.includes(','))
    .sort((a, b) => a.label.localeCompare(b.label));

    // Combine into grouped options: singles first alphabetically, then multiples alphabetically


    const filteredSingles = singles.filter(opt => opt.value !== settings.selectXAxisAnalyte);
    const filteredMultiples = multiples.filter(opt => opt.value !== settings.selectXAxisAnalyte);

    const yAxisAnalyteOptions = [...filteredSingles, ...filteredMultiples];
    const filteredXAxisAnalyteOptions = xAxisAnalyteOptions.filter(opt => opt.value !== settings.selectYAxisAnalyte);

    return (
        <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={5}>
                <FormControl fullWidth>
                <Autocomplete
                    size="small"
                    className={classes?.muiInputs}
                    id="selectConditionCategory"
                    options={displayOptions}
                    value={settings.selectConditionCategory}
                    onChange={(event, newValue) =>
                    setSettings({
                        selectConditionCategory: newValue,
                        selectXAxisAnalyte: '',
                        selectYAxisAnalyte: ''
                    })
                    }
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Select Condition Category"
                    />
                    )}
                    disabled={displayOptions.length === 0}
                />
                </FormControl>
            </Grid>
            <Grid item sm={7} />
            <Grid item xs={12} sm={5}>
                <FormControl fullWidth>
                <Autocomplete
                    size="small"
                    className={classes?.muiInputs}
                    id="selectXAxisAnalyte"
                    options={filteredXAxisAnalyteOptions.length > 0 ? filteredXAxisAnalyteOptions : [{ value: '', label: 'No Data' }]}
                    value={filteredXAxisAnalyteOptions.find(opt => opt.value === settings.selectXAxisAnalyte) || null}
                    onChange={(event, newValue) =>
                    setSettings({
                        ...settings,
                        selectXAxisAnalyte: newValue ? newValue.value : ''
                    })
                    }
                    renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Select X-Axis Analyte"
                        InputProps={{
                        ...params.InputProps,
                        }}
                    />
                    )}
                    disabled={!settings.selectConditionCategory}
                />
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={5}>
                <FormControl fullWidth>
                <Autocomplete
                    size="small"
                    className={classes?.muiInputs}
                    id="selectYAxisAnalyte"
                    options={yAxisAnalyteOptions.length > 0 ? yAxisAnalyteOptions : [{ value: '', label: 'No Data' }]}
                    value={yAxisAnalyteOptions.find(opt => opt.value === settings.selectYAxisAnalyte) || null}
                    onChange={(event, newValue) =>
                        setSettings({
                        ...settings,
                        selectYAxisAnalyte: newValue ? newValue.value : ''
                        })
                    }
                    renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Select Y-Axis Analyte"
                        InputProps={{
                        ...params.InputProps,
                        }}
                    />
                    )}
                    disabled={!settings.selectConditionCategory}
                />
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={() => onSubmit(settings)}
                    disabled={
                        !settings.selectConditionCategory ||
                        !settings.selectXAxisAnalyte ||
                        !settings.selectYAxisAnalyte
                    }
                    >
                    Submit
                </Button>
            </Grid>
        </Grid>
    );
};

export default DualScatterplotsSettingsPanel;